import { useRef, useState } from 'react';
import { subtasksOf, type Task } from '@machs/shared';
import { useSync } from './sync';

/** Gleiche Reihenfolge in Übersicht und Details; erledigte bleiben an ihrem Platz. */
export function useSubtaskSort() {
  const { client } = useSync();
  const [drag, setDrag] = useState<string | null>(null);
  const busy = useRef(false);
  async function move(sourceId: string, target: Task) {
    const source = client.data.task.get(sourceId);
    if (busy.current || !source?.parentId || source.parentId !== target.parentId || source.id === target.id) return;
    const siblings = subtasksOf(client, source.parentId);
    const from = siblings.findIndex(t => t.id === source.id);
    const to = siblings.findIndex(t => t.id === target.id);
    siblings.splice(from, 1);
    siblings.splice(to, 0, source);
    busy.current = true;
    try {
      for (const [index, t] of siblings.entries()) {
        const subtaskOrder = (index + 1) * 1000;
        if (t.subtaskOrder !== subtaskOrder) await client.saveTask({ id: t.id, projectId: t.projectId, title: t.title, subtaskOrder });
      }
    } finally { busy.current = false; }
  }
  return (task: Task) => ({
    draggable: true,
    'data-dragging': drag === task.id || undefined,
    onDragStart: (e: React.DragEvent) => {
      setDrag(task.id);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
    },
    onDragEnd: () => setDrag(null),
    onDragOver: (e: React.DragEvent) => {
      if (drag && client.data.task.get(drag)?.parentId === task.parentId) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault();
      if (drag) void move(drag, task);
      setDrag(null);
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (!e.altKey || !['ArrowUp', 'ArrowDown'].includes(e.key) || !task.parentId) return;
      e.preventDefault();
      const siblings = subtasksOf(client, task.parentId);
      const target = siblings[siblings.findIndex(t => t.id === task.id) + (e.key === 'ArrowUp' ? -1 : 1)];
      if (target) void move(task.id, target);
    },
  });
}
